import type { Prettify } from '../../../../lib/typeUtils';
import type { GetEffectiveDimensionsFactory } from '../coordinates/geometry';
import type { PageDimensions } from '../layout/types';
import {
  hasTransformContribution,
  type TransformContribution,
} from './document-feature';

export type TransformContributionWithState<TState = unknown> = Prettify<
  Omit<TransformContribution<TState>, 'getEffectiveDimensions'> & {
    getEffectiveDimensions: (
      pageId: number,
      baseDims: PageDimensions
    ) => PageDimensions;
  }
>;

export const extractTransformContributions = <TState>(
  features: readonly unknown[],
  getState: () => TState
): TransformContributionWithState[] =>
  features
    .filter(hasTransformContribution)
    .map(feature => ({
      ...feature.transformContribution,
      getEffectiveDimensions: (pageId: number, baseDims: PageDimensions) =>
        feature.transformContribution.getEffectiveDimensions(
          pageId,
          baseDims,
          getState()
        ),
    }))
    .sort((a, b) => a.order - b.order);

/**
 * Composes a GetEffectiveDimensionsFactory from raw feature contributions.
 * Defers state access to the caller, enabling proxy-memoize to track
 * which state properties affect dimensions (e.g. rotation values).
 */
export const composeGetEffectiveDimensions = <TState>(
  features: readonly unknown[]
): GetEffectiveDimensionsFactory<TState> => {
  const rawContributions = features
    .filter(hasTransformContribution)
    .sort(
      (a, b) => a.transformContribution.order - b.transformContribution.order
    )
    .map(f => f.transformContribution.getEffectiveDimensions);

  return rawContributions.length === 0
    ? () => (_, dims) => dims
    : (state: TState) => (pageId, baseDims) =>
        rawContributions.reduce(
          (dims, fn) => fn(pageId, dims, state),
          baseDims
        );
};

export const getTransformWrappers = (
  contributions: TransformContributionWithState[]
): TransformContributionWithState['Wrapper'][] =>
  contributions.map(c => c.Wrapper);
